import { type ClassValue, clsx } from 'clsx'
import { extendTailwindMerge } from 'tailwind-merge'
import type { Config } from 'tailwindcss'

const extendedTheme = {
	colors: {
		border: 'hsl(var(--border))',
		input: {
			DEFAULT: 'hsl(var(--input))',
			invalid: 'hsl(var(--input-invalid))',
		},
		ring: {
			DEFAULT: 'hsl(var(--ring))',
			invalid: 'hsl(var(--foreground-destructive))',
		},
		background: 'hsl(var(--background))',
		foreground: {
			DEFAULT: 'hsl(var(--foreground))',
			destructive: 'hsl(var(--foreground-destructive))',
		},
		primary: {
			DEFAULT: 'hsl(var(--primary))',
			foreground: 'hsl(var(--primary-foreground))',
		},
		secondary: {
			DEFAULT: 'hsl(var(--secondary))',
			foreground: 'hsl(var(--secondary-foreground))',
		},
		destructive: {
			DEFAULT: 'hsl(var(--destructive))',
			foreground: 'hsl(var(--destructive-foreground))',
		},
		muted: {
			DEFAULT: 'hsl(var(--muted))',
			foreground: 'hsl(var(--muted-foreground))',
		},
		accent: {
			DEFAULT: 'hsl(var(--accent))',
			foreground: 'hsl(var(--accent-foreground))',
		},
		popover: {
			DEFAULT: 'hsl(var(--popover))',
			foreground: 'hsl(var(--popover-foreground))',
		},
		card: {
			DEFAULT: 'hsl(var(--card))',
			foreground: 'hsl(var(--card-foreground))',
		},
		sidebar: {
			DEFAULT: 'hsl(var(--sidebar))',
			foreground: 'hsl(var(--sidebar-foreground))',
		},
		content: {
			DEFAULT: 'hsl(var(--content))',
			foreground: 'hsl(var(--content-foreground))',
		},
		success: {
			DEFAULT: 'hsl(var(--success))',
			foreground: 'hsl(var(--success-foreground))',
		},
	},
	borderRadius: {
		lg: 'var(--radius)',
		md: 'calc(var(--radius) - 2px)',
		sm: 'calc(var(--radius) - 4px)',
	},
	fontSize: {
		mega: ['5rem', { lineHeight: '5.25rem', fontWeight: '700' }],
		h1: ['3.5rem', { lineHeight: '3.875rem', fontWeight: '700' }],
		h2: ['2.5rem', { lineHeight: '3rem', fontWeight: '700' }],
		h3: ['2rem', { lineHeight: '2.25rem', fontWeight: '700' }],
		h4: ['1.75rem', { lineHeight: '2.25rem', fontWeight: '700' }],
		h5: ['1.5rem', { lineHeight: '2rem', fontWeight: '700' }],
		h6: ['1rem', { lineHeight: '1.25rem', fontWeight: '700' }],
		'body-2xl': ['2rem', { lineHeight: '2.25rem' }],
		'body-xl': ['1.75rem', { lineHeight: '2.25rem' }],
		'body-lg': ['1.5rem', { lineHeight: '2rem' }],
		'body-md': ['1.25rem', { lineHeight: '1.75rem' }],
		'body-sm': ['1rem', { lineHeight: '1.25rem' }],
		'body-xs': ['0.875rem', { lineHeight: '1.125rem' }],
		'body-2xs': ['0.75rem', { lineHeight: '1rem' }],
		caption: ['1.125rem', { lineHeight: '1.5rem', fontWeight: '600' }],
		button: ['0.75rem', { lineHeight: '1rem', fontWeight: '700' }],
	},
} satisfies Config['theme']

function formatColors() {
	const colors = []
	for (const [key, color] of Object.entries(extendedTheme.colors)) {
		if (typeof color === 'string') {
			colors.push(key)
		} else {
			const colorGroup = Object.keys(color).map((subKey) =>
				subKey === 'DEFAULT' ? '' : subKey,
			)
			colors.push({ [key]: colorGroup })
		}
	}
	return colors
}

const customTwMerge = extendTailwindMerge<string, string>({
	extend: {
		theme: {
			colors: formatColors(),
			borderRadius: Object.keys(extendedTheme.borderRadius),
		},
		classGroups: {
			'font-size': [
				{
					text: Object.keys(extendedTheme.fontSize),
				},
			],
		},
	},
})

export function cn(...inputs: ClassValue[]) {
	return customTwMerge(clsx(inputs))
}
